import clsx from 'clsx';
import Button from 'components/Button';
import Overlay from 'components/Layout/Overlay';
import {TooltipElement} from 'components/Tooltip';
import {TOOLTIPTYPE} from 'components/Tooltip/types';
import {FC, useRef, useState} from 'react';
import {c} from 'ttag';
import {Div, Span} from 'utils/elements';

import {CopyToClipboardProps} from './types';

const CopyToClipboard: FC<CopyToClipboardProps> = ({
    children,
    className,
    disabled,
    elementType = 'button',
    message,
    value,
    ...props
}) => {
    const [showMessage, setShowMessage] = useState(false);

    const labelText = children || c('component').t`Copy`;
    const successMessage = message || c('component').t`Copied to clipboard!`;

    const isCopyDisabled = !!showMessage || disabled;

    const wrapperRef = useRef(null);
    const Wrapper = elementType === 'span' ? Span : Div;

    const handleClick = () => {
        if (isCopyDisabled) return;
        navigator.clipboard.writeText(value);
        setShowMessage(true);
        setTimeout(() => {
            setShowMessage(false);
        }, 1500);
    };

    const element =
        elementType === 'span' ? (
            <span
                className={clsx(
                    isCopyDisabled ? 'text-light-disabled' : 'text-green-700',
                    className,
                )}
                onClick={handleClick}
                onKeyDown={() => {}}
                role="button"
                tabIndex={0}
                {...props}
            >
                {labelText}
            </span>
        ) : (
            <Button
                className={className}
                disabled={isCopyDisabled}
                kind="secondary"
                onClick={handleClick}
                {...props}
            >
                {labelText}
            </Button>
        );

    return (
        <>
            <Wrapper className="inline-flex shrink-0" ref={wrapperRef}>
                {element}
            </Wrapper>
            {showMessage && wrapperRef.current && (
                <Overlay
                    className={clsx('break-words', TOOLTIPTYPE.default)}
                    element={wrapperRef.current}
                    layoutLayer="z-tooltip"
                    layoutMethod="top-or-bottom"
                    layoutOffset={6}
                >
                    {(layout) => (
                        <TooltipElement
                            layout={layout}
                            message={successMessage}
                        />
                    )}
                </Overlay>
            )}
        </>
    );
};

export default CopyToClipboard;
